import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "../lib/api";
import { toast } from "sonner";
import { Wallet, Plus, X, Trash2, AlertCircle } from "lucide-react";

const ESTADOS = ["pendiente", "pagada", "vencida", "anulada"];

export default function AdminCobranza() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        http.get("/admin/cobros"),
        http.get("/admin/cobros/stats/summary"),
      ]);
      setItems(r.data || []);
      setStats(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = filter ? items.filter((c) => c.estado === filter) : items;
  const fmt = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

  const updateEstado = async (id, estado) => {
    try { await http.patch(`/admin/cobros/${id}`, { estado }); toast.success("Actualizado"); load(); }
    catch { toast.error("Error"); }
  };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este cobro?")) return;
    try { await http.delete(`/admin/cobros/${id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("Error"); }
  };

  return (
    <div data-testid="admin-cobranza">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Cobranza</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Cobros, cuotas y pagos asociados a tus clientes.</p>
        </div>
        <button className="btn-primary" onClick={() => setModalOpen(true)} data-testid="btn-new-cobro-global"><Plus size={16}/>Nuevo cobro</button>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total emitido" value={fmt(stats?.total_clp)} color="var(--brand-navy)"/>
        <StatCard label="Pagado" value={fmt(stats?.pagado_clp)} color="#2E7D32"/>
        <StatCard label="Pendiente" value={fmt(stats?.pendiente_clp)} color="#F57C00"/>
        <StatCard label="Vencido" value={fmt(stats?.vencido_clp)} color="#D32F2F" alert/>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-5 flex-wrap">
        <button onClick={() => setFilter("")} className={`yn-btn ${filter === "" ? "yn-btn--active-yes" : ""}`} data-testid="filter-all">Todos</button>
        {ESTADOS.map((e) => (
          <button key={e} onClick={() => setFilter(e)} className={`yn-btn ${filter === e ? "yn-btn--active-yes" : ""}`} data-testid={`filter-${e}`}>{e}</button>
        ))}
      </div>

      <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: "var(--border-subtle)" }}>
        {loading ? <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }}>Cargando…</div>
         : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <Wallet size={36} className="mx-auto mb-3" style={{ color: "var(--text-muted)" }}/>
            <div className="font-bold mb-1" style={{ fontFamily: "Manrope" }}>Sin cobros</div>
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>Crea el primer cobro asociado a un cliente.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead><tr><th>Cliente</th><th>Descripción</th><th>Cuota</th><th>Monto</th><th>Vence</th><th>Estado</th><th>Pago</th><th></th></tr></thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`cobro-${c.id}`}>
                    <td className="font-semibold">
                      {c.cliente_id ? (
                        <Link to={`/admin/comunidades/${c.cliente_id}`} className="nav-link">{c.cliente_nombre}</Link>
                      ) : c.cliente_nombre}
                      <div className="text-[11px] capitalize" style={{ color: "var(--text-muted)" }}>{c.cliente_tipo}</div>
                    </td>
                    <td>{c.descripcion}</td>
                    <td className="text-[13px]">{c.cuota_n}/{c.cuotas_total}</td>
                    <td className="font-semibold">{fmt(c.monto_clp)}</td>
                    <td className="text-[13px]">{c.fecha_vencimiento}</td>
                    <td>
                      <select className="field-input" style={{ padding: "0.3rem 0.5rem", fontSize: 12, width: 120 }} value={c.estado} onChange={(e) => updateEstado(c.id, e.target.value)}>
                        {ESTADOS.map((e) => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </td>
                    <td className="text-[12px]" style={{ color: "var(--text-muted)" }}>
                      {c.fecha_pago || "—"}
                      {c.forma_pago && <div>{c.forma_pago}</div>}
                    </td>
                    <td><button onClick={() => remove(c.id)} style={{ color: "#D32F2F" }}><Trash2 size={14}/></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(stats?.vencido_clp || 0) > 0 && (
        <div className="mt-5 p-4 rounded-xl border flex items-start gap-3" style={{ borderColor: "rgba(211,47,47,0.3)", background: "rgba(211,47,47,0.05)" }}>
          <AlertCircle size={18} style={{ color: "#D32F2F" }} className="mt-0.5"/>
          <div className="text-sm">
            <div className="font-bold mb-1" style={{ fontFamily: "Manrope" }}>Hay cobros vencidos</div>
            <div style={{ color: "var(--text-muted)" }}>Total vencido: {fmt(stats.vencido_clp)}. Contacta a los clientes para regularizar.</div>
          </div>
        </div>
      )}

      {modalOpen && <NewCobroModal onClose={() => setModalOpen(false)} onCreated={() => { load(); setModalOpen(false); }}/>}
    </div>
  );
}

const StatCard = ({ label, value, color, alert }) => (
  <div className="p-5 bg-white border rounded-xl" style={{ borderColor: alert ? "rgba(211,47,47,0.3)" : "var(--border-subtle)" }}>
    <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{ background: color + "22" }}>
      <Wallet size={18} style={{ color }}/>
    </div>
    <div className="text-xl md:text-2xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope", color: alert ? "#D32F2F" : "var(--text-primary)" }}>{value}</div>
    <div className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{label}</div>
  </div>
);

function NewCobroModal({ onClose, onCreated }) {
  const [clientes, setClientes] = useState([]);
  const [form, setForm] = useState({
    cliente_id: "", descripcion: "", monto_clp: "",
    fecha_emision: new Date().toISOString().slice(0,10),
    fecha_vencimiento: "", forma_pago: "", cuota_n: 1, cuotas_total: 1, notas: "",
  });
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    http.get("/admin/clientes").then((r) => setClientes(r.data || []));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, monto_clp: parseFloat(form.monto_clp) || 0,
        cuota_n: parseInt(form.cuota_n) || 1,
        cuotas_total: parseInt(form.cuotas_total) || 1 };
      await http.post("/admin/cobros", payload);
      toast.success("Cobro creado");
      onCreated();
    } catch { toast.error("Error"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10,37,64,0.55)" }} onClick={onClose}>
      <form onSubmit={submit} className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white" style={{ borderColor: "var(--border-subtle)" }}>
          <h3 className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>Nuevo cobro</h3>
          <button type="button" onClick={onClose}><X size={20}/></button>
        </div>
        <div className="p-5 space-y-4">
          <Fld label="Cliente *">
            <select className="field-input" value={form.cliente_id} onChange={(e) => set("cliente_id", e.target.value)} required data-testid="new-cobro-cliente">
              <option value="">Seleccionar cliente...</option>
              {clientes.map((c) => <option key={c.id} value={c.id}>{c.nombre} · {c.rut}</option>)}
            </select>
          </Fld>
          <Fld label="Descripción *"><input className="field-input" value={form.descripcion} onChange={(e) => set("descripcion", e.target.value)} required/></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Monto CLP *"><input type="number" min="0" className="field-input" value={form.monto_clp} onChange={(e) => set("monto_clp", e.target.value)} required/></Fld>
            <Fld label="Forma de pago">
              <select className="field-input" value={form.forma_pago} onChange={(e) => set("forma_pago", e.target.value)}>
                <option value="">—</option>
                <option>Transferencia</option><option>PAT</option><option>PAC</option><option>Cheque</option><option>Efectivo</option>
              </select>
            </Fld>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Emisión *"><input type="date" className="field-input" value={form.fecha_emision} onChange={(e) => set("fecha_emision", e.target.value)} required/></Fld>
            <Fld label="Vencimiento *"><input type="date" className="field-input" value={form.fecha_vencimiento} onChange={(e) => set("fecha_vencimiento", e.target.value)} required/></Fld>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Cuota N°"><input type="number" min="1" className="field-input" value={form.cuota_n} onChange={(e) => set("cuota_n", e.target.value)}/></Fld>
            <Fld label="Total cuotas"><input type="number" min="1" className="field-input" value={form.cuotas_total} onChange={(e) => set("cuotas_total", e.target.value)}/></Fld>
          </div>
        </div>
        <div className="p-5 border-t flex justify-end gap-3 sticky bottom-0 bg-white" style={{ borderColor: "var(--border-subtle)" }}>
          <button type="button" className="btn-outline" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" data-testid="new-cobro-submit">Crear</button>
        </div>
      </form>
    </div>
  );
}

const Fld = ({ label, children }) => (
  <div>
    <label className="field-label">{label}</label>
    {children}
  </div>
);
